import React from 'react';
import { X, AlertTriangle, Check, Trash2 } from 'lucide-react';
import { TrayEdge, TrayNode } from '../../store/editorStore';
import { FIELD_STYLE, ROW, LABEL, SECTION } from './PropertiesEditorStyles';
import {
  getTrayMediaConfig,
  calculateTrayMaxCables,
  calculateTrayFillStatus,
  checkProximitySegregation,
  TrayMedia,
  TrayConstruction,
} from '../../utils/traySegregation.logic';

interface Props {
  edge: TrayEdge;
  sourceNode?: TrayNode;
  targetNode?: TrayNode;
  allEdges?: Record<string, TrayEdge>;
  allNodes?: Record<string, TrayNode>;
  cableCount: number;
  onUpdate: (id: string, updates: Partial<TrayEdge>) => void;
  onDelete: (id: string) => void;
  onClose: () => void;
}

const WIDTH_OPTIONS = [
  { label: '100 мм', value: 0.1 },
  { label: '200 мм', value: 0.2 },
  { label: '300 мм', value: 0.3 },
  { label: '400 мм', value: 0.4 },
  { label: '600 мм', value: 0.6 },
];

const CONSTRUCTION_OPTIONS: { label: string; value: TrayConstruction }[] = [
  { label: 'Оптический короб (Fiber Raceway)', value: 'fiber_raceway' },
  { label: 'Проволочный лоток (Wire Mesh)', value: 'wire_mesh' },
  { label: 'Шинный мост (Power Busway)', value: 'power_busway' },
  { label: 'Лестничный кабель-рост (Ladder)', value: 'ladder' },
  { label: 'Под фальшполом (Underfloor)', value: 'underfloor' },
];

export const TrayInspector: React.FC<Props> = ({
  edge,
  sourceNode,
  targetNode,
  allEdges,
  allNodes,
  cableCount,
  onUpdate,
  onDelete,
  onClose,
}) => {
  const currentMedia = (edge.mediaType || edge.type || 'copper') as TrayMedia;
  const config = getTrayMediaConfig(currentMedia);
  const widthM = edge.widthM || 0.3;
  const maxCables = calculateTrayMaxCables(widthM, currentMedia);
  const fillStatus = calculateTrayFillStatus(cableCount, maxCables);

  // Length in meters (PPM = 100 px/m)
  const lengthM =
    sourceNode && targetNode
      ? +(
          Math.sqrt(
            (targetNode.x - sourceNode.x) ** 2 + (targetNode.y - sourceNode.y) ** 2
          ) / 100
        ).toFixed(2)
      : 0;

  // TIA-942 proximity clearance check against other tray segments
  const proximityViolations: string[] = [];
  if (sourceNode && targetNode && allEdges && allNodes) {
    const currentSegment = {
      id: edge.id,
      type: currentMedia,
      sourcePos: { x: sourceNode.x, y: sourceNode.y },
      targetPos: { x: targetNode.x, y: targetNode.y },
    };
    Object.values(allEdges).forEach(otherEdge => {
      if (otherEdge.id === edge.id) return;
      const s = allNodes[otherEdge.source];
      const t = allNodes[otherEdge.target];
      if (!s || !t) return;
      if (
        otherEdge.source === edge.source ||
        otherEdge.source === edge.target ||
        otherEdge.target === edge.source ||
        otherEdge.target === edge.target
      ) {
        return;
      }
      const otherSegment = {
        id: otherEdge.id,
        type: otherEdge.mediaType || otherEdge.type || 'copper',
        sourcePos: { x: s.x, y: s.y },
        targetPos: { x: t.x, y: t.y },
      };
      const check = checkProximitySegregation(currentSegment, otherSegment, 0.3);
      if (check.hasViolation && check.message) {
        proximityViolations.push(check.message);
      }
    });
  }

  const handleMediaChange = (media: TrayMedia) => {
    const nextConfig = getTrayMediaConfig(media);
    onUpdate(edge.id, {
      type: media,
      mediaType: media,
      trayType: nextConfig.defaultType,
      color: nextConfig.color,
      maxCables: calculateTrayMaxCables(widthM, media),
    });
  };

  const handleWidthChange = (newWidthM: number) => {
    onUpdate(edge.id, {
      widthM: newWidthM,
      maxCables: calculateTrayMaxCables(newWidthM, currentMedia),
    });
  };

  return (
    <div style={{ padding: '12px', fontSize: 12 }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 12,
        }}
      >
        <div style={SECTION}>Кабельная трасса</div>
        <button
          onClick={onClose}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#9ca3af',
            cursor: 'pointer',
            fontSize: 14,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          title="Снять выделение"
        >
          <X size={13} />
        </button>
      </div>

      {/* Media Type Selector */}
      <div style={ROW}>
        <span style={LABEL}>Среда передачи (TIA-942)</span>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 4 }}>
          <button
            onClick={() => handleMediaChange('fiber')}
            style={{
              padding: '6px 4px',
              borderRadius: 4,
              border: `1px solid ${currentMedia === 'fiber' ? '#f59e0b' : '#374151'}`,
              background: currentMedia === 'fiber' ? 'rgba(245, 158, 11, 0.2)' : '#111827',
              color: currentMedia === 'fiber' ? '#fbbf24' : '#9ca3af',
              cursor: 'pointer',
              fontSize: 11,
              fontWeight: currentMedia === 'fiber' ? 600 : 400,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 5,
            }}
          >
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#f59e0b', flexShrink: 0 }} />
            Оптика
          </button>
          <button
            onClick={() => handleMediaChange('copper')}
            style={{
              padding: '6px 4px',
              borderRadius: 4,
              border: `1px solid ${currentMedia === 'copper' ? '#0ea5e9' : '#374151'}`,
              background: currentMedia === 'copper' ? 'rgba(14, 165, 233, 0.2)' : '#111827',
              color: currentMedia === 'copper' ? '#38bdf8' : '#9ca3af',
              cursor: 'pointer',
              fontSize: 11,
              fontWeight: currentMedia === 'copper' ? 600 : 400,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 5,
            }}
          >
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#0ea5e9', flexShrink: 0 }} />
            Медь
          </button>
          <button
            onClick={() => handleMediaChange('power')}
            style={{
              padding: '6px 4px',
              borderRadius: 4,
              border: `1px solid ${currentMedia === 'power' ? '#ef4444' : '#374151'}`,
              background: currentMedia === 'power' ? 'rgba(239, 68, 68, 0.2)' : '#111827',
              color: currentMedia === 'power' ? '#f87171' : '#9ca3af',
              cursor: 'pointer',
              fontSize: 11,
              fontWeight: currentMedia === 'power' ? 600 : 400,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 5,
            }}
          >
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#ef4444', flexShrink: 0 }} />
            Сила
          </button>
        </div>
      </div>

      {/* Construction type */}
      <div style={ROW}>
        <span style={LABEL}>Конструкция лотка</span>
        <select
          value={edge.trayType || config.defaultType}
          onChange={e => onUpdate(edge.id, { trayType: e.target.value as TrayConstruction })}
          style={FIELD_STYLE}
        >
          {CONSTRUCTION_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Width selector */}
      <div style={ROW}>
        <span style={LABEL}>Ширина лотка</span>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 3 }}>
          {WIDTH_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => handleWidthChange(opt.value)}
              style={{
                padding: '4px 2px',
                borderRadius: 4,
                border: `1px solid ${Math.abs(widthM - opt.value) < 0.01 ? '#3b82f6' : '#374151'}`,
                background: Math.abs(widthM - opt.value) < 0.01 ? 'rgba(59, 130, 246, 0.2)' : '#111827',
                color: Math.abs(widthM - opt.value) < 0.01 ? '#60a5fa' : '#9ca3af',
                cursor: 'pointer',
                fontSize: 10,
                fontWeight: Math.abs(widthM - opt.value) < 0.01 ? 600 : 400,
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Geometry and Length */}
      <div style={{ ...ROW, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 8px' }}>
        <div>
          <span style={LABEL}>Длина сегмента</span>
          <div style={{ color: '#f3f4f6', fontFamily: 'JetBrains Mono, monospace', fontSize: 11 }}>
            {lengthM} м
          </div>
        </div>
        <div>
          <span style={LABEL}>Сечение</span>
          <div style={{ color: '#f3f4f6', fontFamily: 'JetBrains Mono, monospace', fontSize: 11 }}>
            {Math.round(widthM * 1000)} мм
          </div>
        </div>
      </div>

      {/* Capacity & Fill (TIA-569) */}
      <div
        style={{
          background: 'rgba(17, 24, 39, 0.7)',
          border: '1px solid #1f2937',
          borderRadius: 6,
          padding: '10px',
          marginBottom: 12,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 6,
          }}
        >
          <span style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>
            Заполнение (TIA-569)
          </span>
          <span
            style={{
              fontSize: 10,
              fontWeight: 600,
              padding: '1px 6px',
              borderRadius: 3,
              background: `${fillStatus.color}22`,
              color: fillStatus.color,
              border: `1px solid ${fillStatus.color}55`,
            }}
          >
            {fillStatus.percent}%
          </span>
        </div>

        {/* Progress bar */}
        <div
          style={{
            height: 6,
            background: '#1f2937',
            borderRadius: 3,
            overflow: 'hidden',
            marginBottom: 8,
          }}
        >
          <div
            style={{
              width: `${Math.min(100, fillStatus.percent)}%`,
              height: '100%',
              background: fillStatus.color,
              borderRadius: 3,
              transition: 'width 0.2s',
            }}
          />
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#94a3b8' }}>
          <span>Проложено: <strong style={{ color: '#f3f4f6' }}>{cableCount} шт.</strong></span>
          <span>Лимит 40%: <strong style={{ color: '#f3f4f6' }}>{maxCables} шт.</strong></span>
        </div>
      </div>

      {/* TIA-942 Proximity Clearance Validation */}
      <div
        style={{
          borderLeft: `3px solid ${proximityViolations.length > 0 ? '#ef4444' : '#22c55e'}`,
          padding: '8px 10px',
          background: proximityViolations.length > 0 ? 'rgba(239, 68, 68, 0.12)' : 'rgba(34, 197, 94, 0.1)',
          borderRadius: '0 4px 4px 0',
          fontSize: 11,
          color: proximityViolations.length > 0 ? '#fca5a5' : '#86efac',
          marginBottom: 12,
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
          {proximityViolations.length > 0 ? (
            <>
              <AlertTriangle size={13} style={{ flexShrink: 0 }} />
              <span>Нарушение TIA-942 (ЭМП-риск)</span>
            </>
          ) : (
            <>
              <Check size={13} style={{ flexShrink: 0 }} />
              <span>TIA-942: Разделение сред соблюдено</span>
            </>
          )}
        </div>
        <div style={{ fontSize: 10, color: '#9ca3af' }}>
          {proximityViolations.length > 0
            ? proximityViolations[0]
            : 'Отступ между силовой и информационной трассой ≥ 300 мм'}
        </div>
      </div>

      {/* Segregation rule note */}
      <div
        style={{
          borderLeft: `3px solid ${config.color}`,
          padding: '6px 8px',
          background: 'rgba(0,0,0,0.2)',
          borderRadius: '0 4px 4px 0',
          fontSize: 10,
          color: '#9ca3af',
          marginBottom: 14,
        }}
      >
        {currentMedia === 'fiber' && 'Оптическая трасса: защита радиуса изгиба, изоляция от медных жгутов.'}
        {currentMedia === 'copper' && 'Информационная трасса: требование отступа >= 300 мм от силовых шин.'}
        {currentMedia === 'power' && 'Силовая трасса: электромагнитный барьер, заземление металлоконструкций.'}
      </div>

      {/* Delete button */}
      <button
        onClick={() => onDelete(edge.id)}
        style={{
          width: '100%',
          padding: '7px',
          background: 'rgba(239, 68, 68, 0.12)',
          border: '1px solid rgba(239, 68, 68, 0.35)',
          color: '#ef4444',
          borderRadius: 5,
          cursor: 'pointer',
          fontSize: 11,
          fontWeight: 500,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
        }}
      >
        <Trash2 size={13} /> Удалить трассу
      </button>
    </div>
  );
};
